import { Avatar, Box, Stack, Tooltip, Typography } from "@mui/material";
import type { ReactElement } from "react";
import {
  COORDINATOR_AVATAR_OVERLAP,
  COORDINATOR_AVATAR_SIZE,
  coordinatorDisplayName,
  safeAvatarSrc,
} from "./dashboardCoordinators";
import type { DashboardCoordinator } from "./dashboardCoordinators";

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

const avatarSx = (size: number) => ({
  width: size,
  height: size,
  fontSize: `${Math.max(9, Math.round(size * 0.43))}px`,
  fontWeight: 700,
  bgcolor: "#333",
  color: "#fff",
  border: "2px solid #000",
  boxSizing: "content-box" as const,
});

type Props = {
  coordinators: DashboardCoordinator[];
  /** Avatar slots available in the cell (from `coordinatorSlotsForWidth`). */
  slots: number;
  /** Avatar diameter in px (default 28). */
  size?: number;
  /** Skip the per-avatar name tooltips (when a parent already shows the names). */
  disableTooltips?: boolean;
};

/**
 * Overlapping coordinator avatars for the dashboard "Coordinators" column.
 * Hover an avatar → name; overflow collapses into a "+N" chip whose tooltip
 * lists the remaining coordinators (stays open while the mouse is over it).
 */
export default function CoordinatorAvatars({
  coordinators,
  slots,
  size = COORDINATOR_AVATAR_SIZE,
  disableTooltips = false,
}: Props) {
  const list = Array.isArray(coordinators) ? coordinators.filter(Boolean) : [];
  if (!list.length) {
    return disableTooltips ? null : (
      <Typography sx={{ opacity: 0.6 }}>—</Typography>
    );
  }

  const overlap = Math.round(
    (COORDINATOR_AVATAR_OVERLAP * size) / COORDINATOR_AVATAR_SIZE,
  );
  const base = avatarSx(size);
  const capacity = Math.max(1, slots);
  const fitsAll = list.length <= capacity;
  const visible = fitsAll ? list : list.slice(0, Math.max(1, capacity - 1));
  const hidden = fitsAll ? [] : list.slice(visible.length);

  const maybeTooltip = (title: ReactElement | string, child: ReactElement) =>
    disableTooltips ? (
      child
    ) : (
      <Tooltip title={title} arrow>
        {child}
      </Tooltip>
    );

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {visible.map((c, i) => {
        const name = coordinatorDisplayName(c);
        return (
          <Box key={c._id ?? i} sx={{ display: "contents" }}>
            {maybeTooltip(
              name,
              <Avatar
                src={safeAvatarSrc(c.profileImage)}
                alt={name}
                sx={{
                  ...base,
                  marginLeft: i === 0 ? 0 : `-${overlap}px`,
                  zIndex: visible.length - i,
                  cursor: "default",
                }}
              >
                {initials(name)}
              </Avatar>,
            )}
          </Box>
        );
      })}
      {hidden.length
        ? maybeTooltip(
            <Stack spacing={0.75} sx={{ py: 0.25 }}>
              {hidden.map((c, i) => {
                const name = coordinatorDisplayName(c);
                return (
                  <Stack
                    key={c._id ?? i}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Avatar
                      src={safeAvatarSrc(c.profileImage)}
                      alt={name}
                      sx={{
                        ...avatarSx(22),
                        border: "none",
                      }}
                    >
                      {initials(name)}
                    </Avatar>
                    <Typography variant="body2">{name}</Typography>
                  </Stack>
                );
              })}
            </Stack>,
            <Avatar
              sx={{
                ...base,
                marginLeft: `-${overlap}px`,
                bgcolor: "#8F00FF",
                cursor: "pointer",
                zIndex: 0,
              }}
            >
              +{hidden.length}
            </Avatar>,
          )
        : null}
    </Box>
  );
}
